import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import type { IMapMarker } from "@Components/Map";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IMultipleSelectOption } from "@Components/MultipleSelect";
import { Panel, TitleAlignment } from "@Components/Panel";

import type { IShoppingMallTile } from "./ShoppingMallTile";
import { ShoppingMallTile } from "./ShoppingMallTile";
import type { IAdditionalService } from "./ShoppingMallsFiltersSearchServices";
import type { IShoppingMallsObservedResult } from "./ShoppingMallsObserved";

import * as css from "./ShoppingMallsFilters.module.css";

export interface IShoppingMallsFiltersMap extends IComponentAjax<IShoppingMallsObservedResult> {
    filteredTableTitle: string;
    noResultsMessage: string;
    onMapMarkerClick: (marker: IMapMarker) => void;
    additionalServiceList: IKeyValueOption[];
    brandsList: IKeyValueOption[];
    selectedServices: IKeyValueOption[];
    selectedBrands: IKeyValueOption[];
}

interface IShoppingMallsFiltersMapState extends IComponentAjaxState {
    items: IShoppingMallTile[];
}

export interface IKeyValueOption extends IAdditionalService, IMultipleSelectOption {
    isPredefined?: boolean;
}

export class ShoppingMallsFiltersMap extends ComponentAjax<
    IShoppingMallsObservedResult,
    IShoppingMallsFiltersMap,
    IShoppingMallsFiltersMapState
> {
    public static readonly defaultProps: IShoppingMallsFiltersMap = {
        filteredTableTitle: null,
        noResultsMessage: null,
        onMapMarkerClick: null,
        additionalServiceList: [],
        brandsList: [],
        selectedServices: [],
        selectedBrands: [],
    };

    constructor(props: IShoppingMallsFiltersMap) {
        super({ ...props, ajaxMethod: AjaxMethod.Post });

        this.state = {
            isLoading:
                this.props.initialData &&
                this.props.initialData.items &&
                this.props.initialData.items.length !== 0,
            items:
                this.props.initialData && this.props.initialData.items
                    ? this.props.initialData.items.slice()
                    : [],
        };
    }

    public componentDidMount() {
        this.fetchData(this.props);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IShoppingMallsFiltersMap) {
        if (this.filtersChanged(nextProps) === true) {
            this.fetchData(nextProps);
        }
    }

    public render(): JSX.Element {
        this.state.items.forEach(
            (item) => (item.onMapLinkClick = (_event, marker) => this.onMapMarkerClick(marker)),
        );

        return (
            <div {...this.rootDefaultAttributes()}>
                <Panel title={this.props.filteredTableTitle} titleAlignment={TitleAlignment.Left}>
                    {this.table}
                </Panel>
                {this.state.isLoading && <Loading />}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: IShoppingMallsObservedResult): void {
        if (!response || !response.items || !response.items.length || response.items.length === 0) {
            this.setState({ isLoading: false, items: [] });
            return;
        }

        this.setState({ isLoading: false, items: response.items });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private filtersChanged(newProps: IShoppingMallsFiltersMap): boolean {
        const mapServices = (arr: IKeyValueOption[]) => arr.map((item) => item.value);

        if (
            mapServices(this.props.selectedServices).join() !== mapServices(newProps.selectedServices).join()
        ) {
            return true;
        }

        if (mapServices(this.props.selectedBrands).join() !== mapServices(newProps.selectedBrands).join()) {
            return true;
        }

        return false;
    }

    private fetchData(nextProps: IShoppingMallsFiltersMap): void {
        const props = !nextProps ? { ...this.props } : { ...nextProps };

        if (this.state.isLoading === false) {
            this.setState({ isLoading: true, items: [] });
        }

        const requestData = {
            predefinedServices: this.props.additionalServiceList.filter((f) => f.isPredefined === true),
            predefinedBrands: this.props.brandsList.filter((b) => b.isPredefined === true),
            services: props.selectedServices,
            brands: props.selectedBrands,
        };

        this.ajaxRequest(requestData);
    }

    private onMapMarkerClick(marker: IMapMarker): void {
        if (this.props.onMapMarkerClick) {
            this.props.onMapMarkerClick.call(this, marker);
        }
    }

    private get table() {
        if (this.state.isLoading === true) {
            return null;
        }

        if (!this.state.items || this.state.items.length === 0) {
            return (
                <div className={`${css.shoppingMallsTable} ${css.noData}`}>{this.props.noResultsMessage}</div>
            );
        }

        return (
            <div className={css.shoppingMallsTable}>
                {this.state.items.map((value) => (
                    <div className={css.wrapper} key={value.id}>
                        <ShoppingMallTile {...value} />
                    </div>
                ))}
            </div>
        );
    }
}
